import { Tag, TagProps, Typography } from 'antd'
import { DrilldownTableExtraColumn } from 'features/drilldown/components/table/config/DrilldownTableConfigContext'
import { PaginatedRolodexTableRecordType } from 'features/drilldown/components/table/PaginatedRolodexTable'
import { unwrapFieldValue } from 'features/libra/ontology/ontologyFieldDefaults'
import type { FieldValue } from 'features/libra/ontology/ontologyFieldTypes'
import { NiiEligibilityTag } from 'features/libra/stepComponents/niiReport/EligibilityTag'
import {
  ELIGIBILITY_LABELS,
  type EligibilityStatus,
} from 'features/libra/types/eligibility'
import startCase from 'lodash/startCase'
import { FC } from 'react'
import styled from 'styled-components'
import { GeckoEntry, RolodexEntryType } from 'types'

export const PlainTextCell = styled.div`
  white-space: pre-wrap;
  word-break: break-word;
`

const ExpandableTextCell = styled(Typography.Paragraph)`
  &&& {
    margin-bottom: 0;
    white-space: pre-wrap;
    word-break: break-word;
  }
`

export function findTicket(
  entries: ReadonlyArray<GeckoEntry>
): GeckoEntry | undefined {
  return entries.find((e) => e.type === RolodexEntryType.LIBRA_TICKET)
}

export function readStringDataField(data: unknown, field: string): string {
  if (!data || typeof data !== 'object') return ''
  const val = (data as Record<string, unknown>)[field]
  return typeof val === 'string' ? val : ''
}

export function readTicketFieldValues(
  entries: ReadonlyArray<GeckoEntry>
): unknown {
  const ticket = findTicket(entries)
  if (!ticket?.data || typeof ticket.data !== 'object') return {}
  const fieldValues = (ticket.data as { fieldValues?: unknown }).fieldValues
  return fieldValues && typeof fieldValues === 'object' ? fieldValues : {}
}

export function unwrapUnknownTicketFieldValue(value: unknown): unknown {
  if (value && typeof value === 'object' && 'value' in value) {
    return unwrapFieldValue(value as FieldValue | undefined)
  }
  return value
}

export function readTicketFieldValueDisplay(
  entries: ReadonlyArray<GeckoEntry>,
  field: string
): string {
  const fieldValues = readTicketFieldValues(entries)
  if (!fieldValues || typeof fieldValues !== 'object') return ''

  const value = unwrapUnknownTicketFieldValue(
    (fieldValues as Record<string, unknown>)[field]
  )
  if (Array.isArray(value)) {
    return value
      .map((item) => String(item).trim())
      .filter(Boolean)
      .join('\n')
  }
  if (value !== undefined && value !== null) {
    return String(value)
  }
  return ''
}

export function readTicketStatusDisplay(
  entries: ReadonlyArray<GeckoEntry>
): string {
  return readStringDataField(findTicket(entries)?.data, 'ticketStatus')
}

export function readEligibilityStatus(
  entries: ReadonlyArray<GeckoEntry>
): EligibilityStatus | undefined {
  const fieldValues = readTicketFieldValues(entries)
  if (!fieldValues || typeof fieldValues !== 'object') return undefined
  const value = unwrapUnknownTicketFieldValue(
    (fieldValues as Record<string, unknown>).eligibility
  )
  if (typeof value !== 'string') return undefined
  return value in ELIGIBILITY_LABELS ? (value as EligibilityStatus) : undefined
}

export function readEligibilityDisplay(
  entries: ReadonlyArray<GeckoEntry>
): string {
  const eligibility = readEligibilityStatus(entries)
  return eligibility ? ELIGIBILITY_LABELS[eligibility] : ''
}

type NiiLibraCell = FC<{ rec: PaginatedRolodexTableRecordType }>

export const renderPlainTextColumn = (
  readValue: (rec: PaginatedRolodexTableRecordType) => string
): NiiLibraCell =>
  (({ rec }) => <PlainTextCell>{readValue(rec)}</PlainTextCell>) as NiiLibraCell

const EXPANDABLE_TEXT_ELLIPSIS = {
  rows: 2,
  expandable: 'collapsible' as const,
  symbol: (expanded: boolean) => (expanded ? 'Show less' : 'Show more'),
}

export const renderExpandableTextColumn = (
  readValue: (rec: PaginatedRolodexTableRecordType) => string
): NiiLibraCell =>
  (({ rec }) => {
    const value = readValue(rec)
    if (!value) return <PlainTextCell />
    return (
      <ExpandableTextCell ellipsis={EXPANDABLE_TEXT_ELLIPSIS}>
        {value}
      </ExpandableTextCell>
    )
  }) as NiiLibraCell

const STATUS_TAG_COLORS: Record<string, TagProps['color']> = {
  approved: 'success',
  pending_review: 'processing',
  in_progress: 'warning',
}

function getStatusTagColor(normalizedStatus: string): TagProps['color'] {
  return STATUS_TAG_COLORS[normalizedStatus] ?? 'default'
}

const renderTicketStatusColumn: DrilldownTableExtraColumn['render'] = ({
  rec: { entries },
}) => {
  const status = readTicketStatusDisplay(entries)
  const normalized = status.trim().toLowerCase()
  const label = normalized === 'approved' ? 'complete' : normalized
  if (!normalized || normalized === 'none') {
    return <PlainTextCell></PlainTextCell>
  }
  return <Tag color={getStatusTagColor(normalized)}>{startCase(label)}</Tag>
}

const renderEligibilityColumn: DrilldownTableExtraColumn['render'] = ({
  rec: { entries },
}) => <NiiEligibilityTag eligibility={readEligibilityStatus(entries)} />

export const NII_LIBRA_TICKET_STATUS_COLUMN: DrilldownTableExtraColumn = {
  id: 'nii_libra_ticket_status',
  name: 'Ticket Status',
  linked_types: [RolodexEntryType.LIBRA_TICKET],
  width: '10rem',
  render: renderTicketStatusColumn,
  exportValue: ({ entries }) => readTicketStatusDisplay(entries),
}

export const NII_LIBRA_ELIGIBILITY_COLUMN: DrilldownTableExtraColumn = {
  id: 'nii_libra_eligibility',
  name: 'Eligibility',
  linked_types: [RolodexEntryType.LIBRA_TICKET],
  width: '8rem',
  render: renderEligibilityColumn,
  exportValue: ({ entries }) => readEligibilityDisplay(entries),
}

export const NII_LIBRA_EXEC_SUMMARY_COLUMN: DrilldownTableExtraColumn = {
  id: 'nii_libra_ticket_exec_summary',
  name: 'Executive Summary',
  linked_types: [RolodexEntryType.LIBRA_TICKET],
  width: '24rem',
  render: renderExpandableTextColumn(({ entries }) =>
    readTicketFieldValueDisplay(entries, 'executive_summary')
  ),
  exportValue: ({ entries }) =>
    readTicketFieldValueDisplay(entries, 'executive_summary'),
}

export const NII_LIBRA_RECOMMENDATIONS_COLUMN: DrilldownTableExtraColumn = {
  id: 'nii_libra_ticket_recommendations',
  name: 'Recommendations',
  linked_types: [RolodexEntryType.LIBRA_TICKET],
  width: '24rem',
  render: renderExpandableTextColumn(({ entries }) =>
    readTicketFieldValueDisplay(entries, 'recommendations')
  ),
  exportValue: ({ entries }) =>
    readTicketFieldValueDisplay(entries, 'recommendations'),
}
